import { useTheme } from '@emotion/react'

import {
  base as arcadeBaseColors,
  component as arcadeComponentColors,
  componentPreMonochrome as arcadeComponentPreMonochrome,
  data as arcadeDataColors,
  semantic as arcadeSemanticColors,
} from '@arcade/web'

import { afterpayColors } from './afterpayColors'
import { bitcoinArcadeColors } from './bitcoinColors'
import { investingArcadeColors } from './investingColors'
import { localArcadeColors } from './localColors'
import { monochromeArcadeColors } from './monochromeColors'
import { taxesArcadeColors } from './taxesColors'

const arcadeColors = {
  ...arcadeSemanticColors,
  ...arcadeComponentColors,
  ...arcadeBaseColors,
  ...arcadeDataColors,
}

export type SemanticColors = keyof typeof arcadeSemanticColors
export type ComponentColors = keyof typeof arcadeComponentColors
export type DataColors = keyof typeof arcadeDataColors
export type BaseColors = keyof typeof arcadeBaseColors
export type ArcadeThemeVariant = 'default' | 'taxes' | 'bitcoin' | 'afterpay' | 'investing' | 'local' | 'monochrome'
export type ArcadeColors = keyof typeof arcadeColors
export type ThemeColors = Record<ArcadeColors, ReturnType<typeof getArcadeThemeColor>>

export type ArcadeThemeColors = {
  semantic: { [key in SemanticColors]: string }
  component: { [key in ComponentColors]: string }
  base: { [key in BaseColors]: string }
  data: { [key in DataColors]: string }
}

export type ArcadeThemeColorsOverride = { [key in ArcadeColors]: { light: string; dark: string } }
export const themeColorExists = (color: ArcadeColors) => arcadeColors[color] != null

/**
 * Returns the arcade color hex value for the given color.
 * This includes arcade colors that are theme variant specific.
 * @param color - The color to get the arcade color for.
 * @returns The arcade color for the given color as a string
 */
export const useGetArcadeColor = (color?: ArcadeColors): string | undefined => {
  // We need to use the theme context to get the arcade colors because some colors might be overridden by the theme variant.
  const { arcadeColors } = useTheme()

  if (!color) {
    return undefined
  }

  if (color in arcadeSemanticColors) {
    return arcadeColors.semantic[color as SemanticColors]
  }
  if (color in arcadeComponentColors) {
    return arcadeColors.component[color as ComponentColors]
  }
  if (color in arcadeBaseColors) {
    return arcadeColors.base[color as BaseColors]
  }
  if (color in arcadeDataColors) {
    return arcadeColors.data[color as DataColors]
  }
  return undefined
}

const getVariantColors = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  let themeColors: Partial<ArcadeThemeColorsOverride>

  switch (themeVariant) {
    case 'afterpay':
      themeColors = afterpayColors
      break
    case 'bitcoin':
      themeColors = bitcoinArcadeColors
      break
    case 'taxes':
      themeColors = taxesArcadeColors
      break
    case 'investing':
      themeColors = investingArcadeColors
      break
    case 'local':
      themeColors = localArcadeColors
      break
    case 'monochrome':
      themeColors = monochromeArcadeColors
      break
    default:
      themeColors = arcadeComponentPreMonochrome
      break
  }

  return getArcadeThemeColors({ themeColors, isDark: darkMode })
}

export const getArcadeColorsByThemeVariant = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  let themeVariantColors = getVariantColors({
    themeVariant,
    darkMode,
  })

  if (themeVariant === 'default') {
    return getBaseArcadeColors({ isDark: darkMode, usePreMonochrome: true })
  } else {
    // For non-default themes, use monochrome base (not pre-monochrome)
    const { semantic, component, base, data } = getBaseArcadeColors({ isDark: darkMode, usePreMonochrome: false })
    const {
      semantic: themeVariantSemantic,
      component: themeVariantComponent,
      base: themeVariantBase,
      data: themeVariantData,
    } = themeVariantColors

    themeVariantColors = {
      semantic: { ...semantic, ...themeVariantSemantic },
      component: { ...component, ...themeVariantComponent },
      base: { ...base, ...themeVariantBase },
      data: { ...data, ...themeVariantData },
    }
    return themeVariantColors
  }
}

export const getOverrideArcadeColorsByThemeVariant = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  return getVariantColors({
    themeVariant,
    darkMode,
  })
}

const getBaseArcadeColors = ({ isDark, usePreMonochrome = true }: { isDark: boolean; usePreMonochrome?: boolean }) => {
  const semantic = Object.keys(arcadeSemanticColors).reduce((set, key) => {
    set[key as ArcadeColors] = getArcadeThemeColor(arcadeSemanticColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  // Merge component colors: start with all monochrome colors, then override with pre-monochrome if requested
  const componentColorSource = usePreMonochrome
    ? { ...arcadeComponentColors, ...arcadeComponentPreMonochrome }
    : arcadeComponentColors

  const component = Object.keys(componentColorSource).reduce((set, key) => {
    set[key as ComponentColors] = getArcadeThemeColor(componentColorSource, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const base = Object.keys(arcadeBaseColors).reduce((set, key) => {
    set[key as BaseColors] = getArcadeThemeColor(arcadeBaseColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const data = Object.keys(arcadeDataColors).reduce(
    (set, key) => {
      set[key as DataColors] = getArcadeThemeColor(arcadeDataColors, isDark, key as DataColors)
      return set
    },
    {} as ArcadeThemeColors['data']
  )

  return {
    semantic,
    component,
    base,
    data,
  }
}

export const getArcadeThemeColors = ({
  themeColors,
  isDark,
}: {
  themeColors: Partial<ArcadeThemeColorsOverride>
  isDark: boolean
}): ArcadeThemeColors => {
  const semantic = Object.keys(themeColors).reduce((set, key) => {
    set[key as ArcadeColors] = getArcadeThemeColor(themeColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const component = Object.keys(themeColors).reduce((set, key) => {
    set[key as ComponentColors] = getArcadeThemeColor(themeColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const base = Object.keys(arcadeBaseColors).reduce((set, key) => {
    set[key as BaseColors] = getArcadeThemeColor(arcadeBaseColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const data = Object.keys(themeColors)
    .filter(key => key in arcadeDataColors)
    .reduce(
      (set, key) => {
        set[key as DataColors] = getArcadeThemeColor(themeColors, isDark, key as DataColors)
        return set
      },
      {} as ArcadeThemeColors['data']
    )

  return {
    semantic,
    component,
    base,
    data,
  }
}

const getArcadeThemeColor = (
  themeColors: Partial<ArcadeThemeColorsOverride>,
  isDark: boolean,
  key: ArcadeColors,
  stack: string[] = []
): string => {
  if (stack.includes(key)) {
    /* eslint-disable-next-line */
    console.warn(`circular color reference [isDark:${isDark}]: [${[...stack, key].join('->')}]`)
    return key
  }

  const semanticColor = themeColors[key]

  const color = (isDark ? semanticColor?.dark : semanticColor?.light) as ArcadeColors
  if (themeColorExists(color)) {
    return getArcadeThemeColor(themeColors, isDark, color, [...stack, key])
  }

  return color
}

export default arcadeColors
